import { Suspense } from 'react';
import type { Card } from '@confluent/shared/lib/conversation-ui/cards';
import { DisclosureBadge } from '../../ui/DisclosureBadge.js';
import { Skeleton } from '../../ui/Skeleton.js';
import { resolveCardRenderer } from './registry.js';
import { UnknownCard } from './UnknownCard.js';

/**
 * Renders a `card` message part through the card registry. The card's own
 * `moduleId` selects a module renderer when one is registered, otherwise the
 * core renderer is used; unknown types degrade to `UnknownCard`.
 */
export function CardPart({
  card,
  conversationId,
  disabled,
}: {
  card: Card;
  conversationId: string;
  disabled?: boolean;
}) {
  const Renderer = resolveCardRenderer(card.type, card.moduleId);
  const body = Renderer ? (
    <Suspense fallback={<Skeleton height={112} rounded="rounded-xl" className="w-full" />}>
      <Renderer card={card} conversationId={conversationId} disabled={disabled} />
    </Suspense>
  ) : (
    <UnknownCard card={card} conversationId={conversationId} disabled={disabled} />
  );
  // The one place that wraps every card: an account persona's disclosure rides in the card's header strip.
  if (!card.disclosure) return body;
  return (
    <div>
      <div className="flex items-center gap-2 px-1 pb-1">
        <DisclosureBadge disclosure={card.disclosure} />
        <span className="truncate text-[11px] text-muted">{card.disclosure.text}</span>
      </div>
      {body}
    </div>
  );
}
